import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { CTU_SPINOFF_COLOR, CTU_STARTUP_COLOR } from '../../lib/colors';
import type { CtuCompany } from '../../types/api';

interface CTUCompaniesByYearChartProps {
  companies: CtuCompany[];
}

interface YearBucket {
  year: number;
  spinoff: number;
  startup: number;
}

export default function CTUCompaniesByYearChart({ companies }: CTUCompaniesByYearChartProps) {
  const { t } = useTranslation('ctuCompanies');
  const currentYear = new Date().getFullYear();

  const data = useMemo<YearBucket[]>(() => {
    const validYears = companies
      .map((c) => c.year)
      .filter((y): y is number => typeof y === 'number' && Number.isFinite(y));
    if (validYears.length === 0) return [];

    const minYear = Math.min(...validYears);
    const maxYear = Math.max(...validYears, currentYear);

    const buckets = new Map<number, YearBucket>();
    for (let y = minYear; y <= maxYear; y++) {
      buckets.set(y, { year: y, spinoff: 0, startup: 0 });
    }
    for (const c of companies) {
      if (c.year == null) continue;
      const bucket = buckets.get(c.year);
      if (!bucket) continue;
      if (c.type === 'Spin-off') bucket.spinoff += 1;
      else if (c.type === 'Start-up') bucket.startup += 1;
    }
    return Array.from(buckets.values());
  }, [companies, currentYear]);

  const showCurrentYearLine =
    data.length > 0 &&
    data[0].year <= currentYear &&
    data[data.length - 1].year >= currentYear;

  const span = data.length;
  const sparseTicks = span > 14;

  const localizedConfig = useMemo<ChartConfig>(
    () => ({
      spinoff: { label: t('chart.spinoff'), color: CTU_SPINOFF_COLOR },
      startup: { label: t('chart.startup'), color: CTU_STARTUP_COLOR },
    }),
    [t],
  );

  return (
    <Card className="border-ctu-border shadow-none mb-6">
      <CardContent className="flex flex-col gap-3 px-6 pt-5 pb-4">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {t('chart.title')}
          </span>
          <span className="text-xs text-ctu-text-muted">{t('chart.subtitle')}</span>
        </div>

        {data.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-ctu-text-muted">
            {t('table.empty')}
          </div>
        ) : (
          <ChartContainer config={localizedConfig} className="aspect-auto w-full h-[320px]">
            <BarChart
              data={data}
              margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
              barCategoryGap={2}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={sparseTicks ? 'preserveStartEnd' : 0}
                minTickGap={sparseTicks ? 24 : 0}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={36}
                allowDecimals={false}
                tickMargin={4}
              />
              <ChartTooltip
                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    labelFormatter={(value) => String(value)}
                  />
                }
              />
              <ChartLegend
                content={<ChartLegendContent />}
                verticalAlign="bottom"
                height={28}
              />
              <Bar
                dataKey="spinoff"
                stackId="companies"
                fill="var(--color-spinoff)"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="startup"
                stackId="companies"
                fill="var(--color-startup)"
                radius={[2, 2, 0, 0]}
              />
              {showCurrentYearLine && (
                <ReferenceLine
                  x={currentYear}
                  stroke="hsl(var(--foreground))"
                  strokeDasharray="4 4"
                  strokeOpacity={0.45}
                  label={{
                    value: t('chart.currentYear'),
                    position: 'top',
                    fill: 'hsl(var(--foreground))',
                    fillOpacity: 0.6,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              )}
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
